import { useState } from "react";
import { BookOpen, Bot, Eye, Target } from "lucide-react";
import { motion } from "framer-motion";
import {
  CONSUMER_CHALLENGES,
  INSTITUTION_CHALLENGES,
  INTRO_MEDIA,
  OBJECTIVES,
  VISION_PILLARS,
} from "../../constants/simmarkets";
import { ArrowIcon, CornerMarks, MediaSlot, SectionShell, useFade } from "./ui";

const PILLAR_ICONS = {
  book: BookOpen,
  eye: Eye,
  bot: Bot,
  target: Target,
};

function MarketStory() {
  const { fade } = useFade();
  const [audience, setAudience] = useState(OBJECTIVES[0].id);
  const active = OBJECTIVES.find((item) => item.id === audience) || OBJECTIVES[0];

  return (
    <>
      <SectionShell id="intro" labelledBy="intro-heading" tone="white">
        <div className="relative mx-auto grid max-w-8xl items-stretch gap-10 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-12 lg:px-8 lg:py-28">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Introduction
            </p>
            <h2
              id="intro-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Smarter financial decisions
              <span className="mt-1 block text-market-green">start here</span>
            </h2>
            <p className="mt-5 max-w-xl text-lg font-medium leading-snug text-navy/70">
              Choosing the right financial product should never be confusing.
            </p>
            <div className="mt-6 max-w-xl space-y-4 text-base leading-[1.8] text-navy/65">
              <p>
                Unfortunately, many consumers struggle to compare products, understand financial
                terminology or identify trustworthy providers. At the same time, financial
                institutions spend significant resources acquiring customers who may never convert.
              </p>
              <p>
                SimMarket bridges this gap by creating an intelligent marketplace where consumers
                receive the information, education and guidance they need before making financial
                decisions.
              </p>
              <p>
                Powered by Artificial Intelligence and advanced customer engagement technologies,
                SimMarket enables consumers to discover products, compare providers, ask questions,
                receive personalized recommendations and connect directly with verified financial
                institutions.
              </p>
              <p>
                For financial institutions, SimMarket becomes an intelligent customer acquisition
                engine that delivers highly qualified leads, improves conversion rates and reduces
                acquisition costs through intent-driven engagement rather than traditional
                advertising.
              </p>
            </div>
            <p className="mt-6 max-w-xl border-l-2 border-market-gold pl-5 text-base font-semibold leading-snug tracking-tight text-navy">
              This makes SimMarket much more than a marketplace—it is a complete customer
              acquisition, education and engagement ecosystem.
            </p>
          </motion.div>

          <motion.div
            className="relative min-h-[22rem]"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <CornerMarks />
            <MediaSlot
              src={INTRO_MEDIA.src}
              alt={INTRO_MEDIA.alt}
              label="Introduction"
              spec={INTRO_MEDIA.spec}
              className="h-full min-h-[22rem] w-full lg:absolute lg:inset-0"
              tone="light"
            />
          </motion.div>
        </div>
      </SectionShell>

      <SectionShell id="why-simmarkets" labelledBy="why-heading" tone="mist">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Why SimMarket matters
            </p>
            <h2
              id="why-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              The challenge facing today&apos;s
              <span className="mt-1 block">financial services industry</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Consumers today have access to thousands of financial products, but more choices do
              not necessarily lead to better decisions.
            </p>
          </motion.header>

          <div className="mt-12 grid gap-px overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-2">
            <ChallengePanel
              index="01"
              title="Consumers struggle with"
              items={CONSUMER_CHALLENGES}
              fade={fade}
            />
            <ChallengePanel
              index="02"
              title="Institutions continue to experience"
              items={INSTITUTION_CHALLENGES}
              fade={fade}
              dark
            />
          </div>

          <motion.div
            className="mt-8 border border-navy/10 bg-white px-6 py-8 sm:px-8"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="max-w-3xl text-base leading-relaxed text-navy/70 sm:text-lg">
              SimMarket addresses these challenges by creating an intelligent ecosystem where
              education comes before sales, trust comes before transactions and quality replaces
              quantity.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-3">
              {[
                "Consumers become more informed.",
                "Institutions acquire better customers.",
                "Everyone benefits from smarter financial engagement.",
              ].map((line, index) => (
                <li key={line} className="border-l-2 border-market-gold pl-4">
                  <span className="block font-mono text-[11px] tracking-[0.18em] text-market-green">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="mt-1 block text-sm font-semibold tracking-tight text-navy">
                    {line}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </SectionShell>

      <SectionShell id="vision" labelledBy="vision-heading" tone="navy">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-market-gold to-transparent"
            aria-hidden="true"
          />
          <p
            className="pointer-events-none absolute -left-2 top-10 select-none text-[7rem] font-black leading-none tracking-tight text-white/[0.045] sm:text-[10rem] lg:top-6 lg:text-[12rem]"
            aria-hidden="true"
          >
            VISION
          </p>

          <div className="relative border border-white/10 bg-white/[0.03] p-6 sm:p-10 lg:p-12">
            <CornerMarks dark />

            <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:gap-14">
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fade}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-market-gold">
                  Our vision
                </p>
                <h2
                  id="vision-heading"
                  className="mt-6 font-semibold leading-[1.18] tracking-tight text-white [font-size:clamp(1.55rem,3.2vw,2.55rem)]"
                >
                  To become Africa&apos;s leading{" "}
                  <span className="text-market-gold">intelligent financial marketplace</span> that
                  empowers consumers through education, transparency and{" "}
                  <span className="text-market-gold">AI-powered financial decision-making</span> while
                  helping financial institutions grow through{" "}
                  <span className="text-market-gold">qualified customer acquisition</span>.
                </h2>
                <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                  <li className="border-l-2 border-market-gold pl-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-market-gold">
                      Consumers
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-white/65">
                      Education, transparency and AI-powered decisions.
                    </p>
                  </li>
                  <li className="border-l-2 border-market-green pl-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9bd4c4]">
                      Institutions
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-white/65">
                      Growth through qualified customer acquisition.
                    </p>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                className="relative mx-auto w-full max-w-[22rem]"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fade}
              >
                <VisionCompass />
              </motion.div>
            </div>

            <motion.ol
              className="relative mt-10 grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4"
              aria-label="Vision pillars"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              {VISION_PILLARS.map((item) => {
                const Icon = PILLAR_ICONS[item.icon] || Target;
                return (
                  <li key={item.n} className="bg-white/[0.06] px-5 py-6 sm:px-6">
                    <span className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[11px] tracking-[0.18em] text-market-gold">
                        {item.n}
                      </span>
                      <Icon className="h-4 w-4 text-market-gold" strokeWidth={1.75} aria-hidden="true" />
                    </span>
                    <p className="mt-4 text-base font-semibold tracking-tight text-white">{item.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">{item.copy}</p>
                  </li>
                );
              })}
            </motion.ol>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="objectives" labelledBy="objectives-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Service objectives
            </p>
            <h2
              id="objectives-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Designed for every side
              <span className="mt-1 block">of the marketplace</span>
            </h2>
          </motion.header>

          <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Audience objectives">
            {OBJECTIVES.map((item) => {
              const selected = item.id === active.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setAudience(item.id)}
                  className={`inline-flex min-h-11 cursor-pointer items-center rounded-full px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold ${
                    selected
                      ? "bg-market-green text-white"
                      : "border border-navy/10 bg-white text-navy/65 hover:border-navy/30"
                  }`}
                >
                  {item.title}
                </button>
              );
            })}
          </div>

          <motion.div
            key={active.id}
            className="mt-8 grid gap-8 border border-navy/10 bg-[#f3f7f5] p-6 sm:p-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]"
            initial={false}
            animate={{ opacity: 1 }}
          >
            <div>
              <p className="font-mono text-[11px] tracking-[0.2em] text-market-green">{active.index}</p>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy">{active.title}</h3>
              <p className="mt-3 max-w-md text-base leading-relaxed text-navy/60">{active.lede}</p>
            </div>
            <ul className="grid gap-2 sm:grid-cols-2">
              {active.items.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 border border-navy/10 bg-white px-4 py-3 text-sm leading-snug text-navy/75"
                >
                  <ArrowIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-market-gold" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </SectionShell>
    </>
  );
}

function ChallengePanel({ index, title, items, fade, dark = false }) {
  return (
    <motion.div
      className={`px-6 py-8 sm:px-8 sm:py-10 ${dark ? "bg-navy text-white" : "bg-white text-navy"}`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={fade}
    >
      <p
        className={`font-mono text-[11px] tracking-[0.2em] ${dark ? "text-market-gold" : "text-market-green"}`}
      >
        {index}
      </p>
      <h3 className="mt-3 text-2xl font-semibold tracking-tight">{title}</h3>
      <ul className="mt-6 space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm leading-relaxed sm:text-base">
            <span
              className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${dark ? "bg-market-gold" : "bg-market-green"}`}
            />
            <span className={dark ? "text-white/75" : "text-navy/70"}>{item}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default MarketStory;

function VisionCompass() {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[22rem]">
      <p className="absolute left-1/2 top-0 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.22em] text-market-gold">
        Education
      </p>
      <p className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.22em] text-market-gold">
        Intelligence
      </p>
      <p className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-[0.22em] text-market-gold [writing-mode:vertical-rl]">
        Transparency
      </p>
      <p className="absolute left-0 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-[0.22em] text-market-gold [writing-mode:vertical-rl]">
        Acquisition
      </p>
      <svg viewBox="0 0 320 320" className="absolute inset-[16%]" aria-hidden="true" fill="none">
        <circle cx="160" cy="160" r="148" stroke="rgba(229,184,11,0.2)" strokeWidth="1" />
        <circle cx="160" cy="160" r="108" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
        <circle cx="160" cy="160" r="64" stroke="rgba(52,132,110,0.8)" strokeWidth="1.6" />
        <line x1="160" y1="12" x2="160" y2="308" stroke="rgba(255,255,255,0.14)" />
        <line x1="12" y1="160" x2="308" y2="160" stroke="rgba(255,255,255,0.14)" />
        <line x1="56" y1="56" x2="264" y2="264" stroke="rgba(229,184,11,0.16)" />
        <line x1="264" y1="56" x2="56" y2="264" stroke="rgba(229,184,11,0.16)" />
        <circle cx="160" cy="12" r="6" fill="#E5B80B" />
        <circle cx="308" cy="160" r="6" fill="#E5B80B" />
        <circle cx="160" cy="308" r="6" fill="#E5B80B" />
        <circle cx="12" cy="160" r="6" fill="#E5B80B" />
        <circle cx="160" cy="160" r="36" fill="#E5B80B" />
        <text
          x="160"
          y="164"
          textAnchor="middle"
          fill="#0c1f1b"
          fontSize="10"
          fontWeight="700"
          letterSpacing="0.08em"
        >
          AFRICA
        </text>
      </svg>
    </div>
  );
}
